import { FunctionComponent, memo, useMemo } from "react";
import { IProps } from "./comparison-tags.types";
import { Page, PageContent, PageHeader } from "@edgetier/client-components";
import useTagGroups from "hooks-for/tag-groups/use-tag-groups";
import useTags from "hooks-for/tags/use-tags";
import { SpinnerUntil } from "@edgetier/components";
import { IInteractionMetricsAvailable, ITag, ITagGroup } from "@edgetier/watchtower-types";
import { useInteractionMetricsAvailable } from "hooks-for/metrics/use-interaction-metrics-available";
import { useLocation } from "react-router-dom";
import ExploreFilters from "components-for/filters/explore-filters";
import TagsTable from "./tags-table";
import TagGroupFilter from "./tag-group-filter";
import { isTagGroupInUrl } from "./comparison-tags.utiilties";
import "./comparison-tags.scss";

/**
 * List of all the tags in the selected tag group with data that can be used to
 * compare them against each other.
 */
const ComparisonTags: FunctionComponent<IProps> = () => {
    const { search } = useLocation();
    const { data: tagGroups, isError: isTagGroupsError, isLoading: areTagGroupsLoading } = useTagGroups();
    const { data: tags, isError: isTagsError, isLoading: areTagsLoading } = useTags();

    const isTagGroupSelected = useMemo(() => isTagGroupInUrl(search), [search]);
    const {
        data: interactionMetricsAvailable,
        isError: isInteractionMetricsAvailableError,
        isLoading: areInteractionMetricsAvailableLoading,
    } = useInteractionMetricsAvailable();

    return (
        <Page>
            <PageHeader title="Tags" />
            <PageContent>
                <SpinnerUntil<{
                    tags: ITag[];
                    tagGroups: ITagGroup[];
                    interactionMetricsAvailable: IInteractionMetricsAvailable;
                }>
                    data={{ tagGroups, tags, interactionMetricsAvailable }}
                    isReady={!areTagGroupsLoading && !areTagsLoading && !areInteractionMetricsAvailableLoading}
                >
                    {(data) =>
                        isTagsError || isTagGroupsError || isInteractionMetricsAvailableError ? (
                            "Error"
                        ) : (
                            <div className="comparison-tags">
                                <div className="comparison-tags__select-tag-group">
                                    <TagGroupFilter />
                                </div>

                                <div className="comparison-tags__group-details">
                                    <ExploreFilters
                                        tags={data.tags || []}
                                        tagGroups={data.tagGroups || []}
                                        visibleFields={["interactionType"]}
                                        limitSelectionMonths={3}
                                    />

                                    {!isTagGroupSelected && (
                                        <p className="comparison-tags__not-tag-group">
                                            Please select a tag group to review
                                        </p>
                                    )}

                                    {isTagGroupSelected && <TagsTable />}
                                </div>
                            </div>
                        )
                    }
                </SpinnerUntil>
            </PageContent>
        </Page>
    );
};

export default memo(ComparisonTags);
